import type { TokenSymbol } from "../../lib/constants";
import type { Address, Hex } from "viem";

export type OrderStatus = "active" | "filled" | "cancelled" | "expired";

export interface DutchOrder {
  id: string;
  maker: Address;
  inputToken: TokenSymbol;
  outputToken: TokenSymbol;
  inputAmount: string;
  minOutput: string;
  startAmount: string;
  endAmount: string;
  decayStartTime: number;
  decayEndTime: number;
  /** Deadline timestamp — order expires if unfilled by this time */
  deadline: number;
  reactor: string;
  signature: Hex;
  status: OrderStatus;
  createdAt: number;
  /** Timestamp when the order was filled */
  filledAt?: number;
  /** Actual output amount received on fill */
  fillOutputAmount?: string;
  /** Address of the filler (simulated) */
  fillerAddress?: Address;
  /** Transaction hash for the fill */
  fillTxHash?: Hex;
}

export const REACTOR_DESCRIPTIONS: Record<string, string> = {
  exclusiveDutchOrderReactor: "Exclusive Dutch Order Reactor",
  priorityOrderReactor: "Priority Order Reactor",
  v2DutchOrderReactor: "V2 Dutch Order Reactor",
  orderQuoter: "Order Quoter",
};

export const REACTOR_SUBTITLES: Record<string, string> = {
  exclusiveDutchOrderReactor:
    "Dedicated filler gets an exclusive priority period before open competition",
  priorityOrderReactor:
    "MEV-aware ordering — fillers compete with priority fees for better execution",
  v2DutchOrderReactor:
    "Enhanced decay with multiple output curves and gas-adjusted pricing",
  orderQuoter:
    "Quote-only reactor for price discovery — does not execute orders",
};

export const STATUS_VARIANTS: Record<
  OrderStatus,
  "success" | "warning" | "error" | "info"
> = {
  active: "warning",
  filled: "success",
  cancelled: "error",
  expired: "info",
};

export const STATUS_COLORS: Record<OrderStatus, string> = {
  active: "#C9A84C",
  filled: "#34d16a",
  cancelled: "#ef4444",
  expired: "#6b7280",
};

/** Duration presets in seconds */
export const DURATION_PRESETS = [
  { label: "1 min", value: 60 },
  { label: "5 min", value: 300 },
  { label: "15 min", value: 900 },
  { label: "1 hr", value: 3600 },
] as const;

/** Compute current effective output at a given elapsed time */
export function computeCurrentOutput(
  startAmount: number,
  endAmount: number,
  decayDuration: number,
  elapsedSeconds: number,
): number {
  if (decayDuration <= 0) return startAmount;
  const progress = Math.min(Math.max(elapsedSeconds / decayDuration, 0), 1);
  return startAmount + (endAmount - startAmount) * progress;
}

/** Format seconds into human-readable duration */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`;
  if (seconds < 3600) {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return sec > 0 ? `${String(min)}m ${String(sec)}s` : `${String(min)}m`;
  }
  const hr = Math.floor(seconds / 3600);
  const min = Math.floor((seconds % 3600) / 60);
  return min > 0 ? `${String(hr)}h ${String(min)}m` : `${String(hr)}h`;
}

/** Format relative time (e.g. "2 min ago", "Feb 27, 10:53 AM") */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${String(Math.floor(diffSec / 60))} min ago`;
  if (diffSec < 86400) return `${String(Math.floor(diffSec / 3600))}h ago`;

  // Older than 24h — show absolute
  const d = new Date(timestamp);
  const month = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${month} ${String(day)}, ${time}`;
}
